import React, { useEffect, useState } from 'react';
import { useBasicFilters, useUpdateBasicFilters, useResetBasicFilters } from '@/hooks/api';
import { useFilters, useUI, useAppStore } from '@/store';
import { Button, LoadingScreen } from '@/components/ui';
import { TelegramUtils } from '@/utils/telegram';
import { api } from '@/api';
import type { BasicFilters } from '@/types';

interface DictionaryItem {
  id: number;
  name: string;
  icon?: string;
}

const FiltersScreen: React.FC = () => {
  const { setActiveScreen } = useUI();
  const { setBasicFilters, setHasActiveFilters } = useFilters();
  const { data: savedFilters, isLoading } = useBasicFilters();
  const updateFilters = useUpdateBasicFilters();
  const resetFilters = useResetBasicFilters();
  
  // Local state for form
  const [minAge, setMinAge] = useState(18);
  const [maxAge, setMaxAge] = useState(100);
  const [lifestyleTagIds, setLifestyleTagIds] = useState<number[]>([]);
  const [meetingGoalIds, setMeetingGoalIds] = useState<number[]>([]);
  
  // Dictionary data
  const [lifestyleTags, setLifestyleTags] = useState<DictionaryItem[]>([]);
  const [meetingGoals, setMeetingGoals] = useState<DictionaryItem[]>([]);
  const [loadingDictionaries, setLoadingDictionaries] = useState(true);

  // Load dictionaries
  useEffect(() => {
    const loadDictionaries = async () => {
      try {
        const [lifestyle, goals] = await Promise.all([
          api.getLifestyleTags(),
          api.getMeetingGoals(),
        ]);
        setLifestyleTags(lifestyle);
        setMeetingGoals(goals);
      } catch (error) {
        console.error('Failed to load dictionaries:', error);
      } finally {
        setLoadingDictionaries(false);
      }
    };
    loadDictionaries();
  }, []);

  // Load saved filters
  useEffect(() => {
    if (savedFilters) {
      setMinAge(savedFilters.min_age);
      setMaxAge(savedFilters.max_age);
      setLifestyleTagIds(savedFilters.lifestyle_tag_ids || []);
      setMeetingGoalIds(savedFilters.meeting_goal_ids || []);
    }
  }, [savedFilters]);

  // Configure Telegram UI
  useEffect(() => {
    TelegramUtils.configureBackButton(true, () => {
      setActiveScreen('feed');
    });
    TelegramUtils.configureMainButton({ visible: false });
  }, [setActiveScreen]);

  // Check if filters are active
  const hasActiveFiltersState = 
    minAge !== 18 || 
    maxAge !== 100 || 
    lifestyleTagIds.length > 0 || 
    meetingGoalIds.length > 0;

  const handleApplyFilters = async () => {
    const filters: Partial<BasicFilters> = {
      min_age: minAge,
      max_age: maxAge,
      lifestyle_tag_ids: lifestyleTagIds,
      meeting_goal_ids: meetingGoalIds,
    };

    updateFilters.mutate(filters, {
      onSuccess: () => {
        // Update store
        setBasicFilters(filters as BasicFilters);
        setHasActiveFilters(hasActiveFiltersState);
        
        // Go back to feed
        setActiveScreen('feed');
      },
    });
  };

  const handleResetFilters = async () => {
    const confirmed = await TelegramUtils.showConfirm(
      'Сбросить все фильтры к значениям по умолчанию?'
    );
    
    if (!confirmed) return;

    resetFilters.mutate(undefined, {
      onSuccess: () => {
        // Reset local state
        setMinAge(18);
        setMaxAge(100);
        setLifestyleTagIds([]);
        setMeetingGoalIds([]);
        
        // Update store
        setBasicFilters({
          min_age: 18,
          max_age: 100,
          lifestyle_tag_ids: [],
          meeting_goal_ids: [],
        });
        setHasActiveFilters(false);
        
        // Go back to feed
        setActiveScreen('feed');
      },
    });
  };

  const toggleLifestyleTag = (tagId: number) => {
    if (lifestyleTagIds.includes(tagId)) {
      setLifestyleTagIds(lifestyleTagIds.filter(id => id !== tagId));
    } else {
      setLifestyleTagIds([...lifestyleTagIds, tagId]);
    }
    TelegramUtils.haptic.selection();
  };

  const selectAnyLifestyle = () => {
    setLifestyleTagIds([]);
    TelegramUtils.haptic.selection();
  };

  const toggleMeetingGoal = (goalId: number) => {
    if (meetingGoalIds.includes(goalId)) {
      setMeetingGoalIds(meetingGoalIds.filter(id => id !== goalId));
    } else {
      setMeetingGoalIds([...meetingGoalIds, goalId]);
    }
    TelegramUtils.haptic.selection();
  };

  if (isLoading || loadingDictionaries) {
    return <LoadingScreen message="Загрузка фильтров..." />;
  }

  return (
    <div className="min-h-screen bg-tg-bg safe-area-y">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/5">
        <button
          onClick={() => setActiveScreen('feed')}
          className="p-2 -ml-2 hover:bg-white/5 rounded-xl transition-colors"
        >
          <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        
        <h1 className="text-xl font-bold text-white absolute left-1/2 transform -translate-x-1/2">Фильтры</h1>
        
        {hasActiveFiltersState && (
          <button
            onClick={handleResetFilters}
            disabled={resetFilters.isPending}
            className="text-sm text-white font-semibold hover:text-white/80 transition-colors disabled:opacity-50"
          >
            Сбросить
          </button>
        )}
        {!hasActiveFiltersState && <div className="w-20" />}
      </div>

      {/* Content */}
      <div className="p-4 pb-32 space-y-6">
        {/* Age Section */}
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-white">Возраст</h2>
          
          <div className="space-y-4">
            {/* Min Age */}
            <div>
              <label className="block text-sm text-white/70 mb-2">
                От {minAge} лет
              </label>
              <input
                type="range"
                min="18"
                max="100"
                value={minAge}
                onChange={(e) => {
                  const value = Number(e.target.value);
                  if (value <= maxAge) {
                    setMinAge(value);
                    TelegramUtils.haptic.selection();
                  }
                }}
                className="w-full h-2 bg-white/10 rounded-lg appearance-none cursor-pointer accent-white"
              />
            </div>

            {/* Max Age */}
            <div>
              <label className="block text-sm text-white/70 mb-2">
                До {maxAge} лет
              </label>
              <input
                type="range"
                min="18"
                max="100"
                value={maxAge}
                onChange={(e) => {
                  const value = Number(e.target.value);
                  if (value >= minAge) {
                    setMaxAge(value);
                    TelegramUtils.haptic.selection();
                  }
                }}
                className="w-full h-2 bg-white/10 rounded-lg appearance-none cursor-pointer accent-white"
              />
            </div>
          </div>
        </div>

        {/* Lifestyle Section */}
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-white">Образ жизни</h2>
          <p className="text-sm text-white/60">Можно выбрать несколько</p>
          
          <div className="space-y-2">
            {/* Any option */}
            <button
              onClick={selectAnyLifestyle}
              className={`w-full p-4 rounded-2xl border transition-all ${
                lifestyleTagIds.length === 0
                  ? 'bg-white/10 border-white/20'
                  : 'bg-white/5 border-white/10 hover:bg-white/10'
              }`}
            >
              <div className="flex items-center justify-between">
                <span className="text-white font-medium">Любой</span>
                {lifestyleTagIds.length === 0 && (
                  <div className="w-5 h-5 rounded-full bg-white flex items-center justify-center">
                    <svg className="w-3 h-3 text-black" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                )}
              </div>
            </button>

            {/* Lifestyle options */}
            {lifestyleTags.map((tag) => (
              <button
                key={tag.id}
                onClick={() => toggleLifestyleTag(tag.id)}
                className={`w-full p-4 rounded-2xl border transition-all ${
                  lifestyleTagIds.includes(tag.id)
                    ? 'bg-white/10 border-white/20'
                    : 'bg-white/5 border-white/10 hover:bg-white/10'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-white font-medium">
                    {tag.icon} {tag.name}
                  </span>
                  {lifestyleTagIds.includes(tag.id) && (
                    <div className="w-5 h-5 rounded-full bg-white flex items-center justify-center">
                      <svg className="w-3 h-3 text-black" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Meeting Goals Section */}
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-white">Цели знакомства</h2>
          <p className="text-sm text-white/60">Можно выбрать несколько</p>
          
          <div className="space-y-2">
            {meetingGoals.map((goal) => (
              <button
                key={goal.id}
                onClick={() => toggleMeetingGoal(goal.id)}
                className={`w-full p-4 rounded-2xl border transition-all ${
                  meetingGoalIds.includes(goal.id)
                    ? 'bg-white/10 border-white/20'
                    : 'bg-white/5 border-white/10 hover:bg-white/10'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-white font-medium">
                    {goal.icon} {goal.name}
                  </span>
                  {meetingGoalIds.includes(goal.id) && (
                    <div className="w-5 h-5 rounded-full bg-white flex items-center justify-center">
                      <svg className="w-3 h-3 text-black" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Apply Button */}
      <div className="fixed bottom-0 left-0 right-0 safe-area-bottom z-50">
        <div className="p-4 bg-tg-bg/95 backdrop-blur-xl border-t border-white/5">
          <button
            onClick={handleApplyFilters}
            disabled={updateFilters.isPending}
            className="w-full h-14 bg-white text-black rounded-2xl font-bold text-base hover:bg-neutral-100 transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {updateFilters.isPending ? 'Применение...' : 'Применить фильтры'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FiltersScreen;

